import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  AlertCircle,
  Download
} from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { getAllowedBranches, getAllowedYears } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import StudentProgressFilters from './StudentProgressFilters';
import './StudentProgress.css';

const StudentProgressTable = () => {
  // Get current user for HOD filtering
  const { user } = useAuth();
  const allowedBranches = getAllowedBranches(user?.email);
  const allowedYears = getAllowedYears(user?.email);
  
  // State management
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentSort, setCurrentSort] = useState({ by: 'name', order: 'asc' });
  
  // Filter states
  const [selectedFilters, setSelectedFilters] = useState({
    year: null,
    branch: null,
    domain: null,
    activity: null,
    search: '',
  });
  
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Fetch filters on component mount and when user changes
  useEffect(() => {
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Auto-select first branch/year for HODs when filters load
  useEffect(() => {
    if (filters) {
      const updates = {};
      
      // Auto-select first allowed branch for HOD with branch restrictions (not null)
      if (allowedBranches && allowedBranches.length > 0 && !selectedFilters.branch) {
        updates.branch = allowedBranches[0];
      }
      
      // Auto-select first allowed year for HOD with year restrictions (not null)
      if (allowedYears && allowedYears.length > 0 && !selectedFilters.year) {
        updates.year = String(allowedYears[0]);
      }
      
      if (Object.keys(updates).length > 0) {
        setSelectedFilters(prev => ({ ...prev, ...updates }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, allowedBranches, allowedYears]);

  // Fetch students when filters or pagination changes
  useEffect(() => {
    if (filters) {
      fetchStudentProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, currentPage, perPage, currentSort, filters]);

  const fetchFilters = async () => {
    try {
      const response = await dashboardAPI.getStudentFilters();
      if (response.success) {
        // Filter branches and years based on HOD permissions
        const filteredData = {
          ...response.data,
          branches: allowedBranches 
            ? response.data.branches.filter(branch => allowedBranches.includes(branch))
            : response.data.branches,
          years: allowedYears
            ? response.data.years.filter(year => allowedYears.includes(String(year)))
            : response.data.years
        };
        setFilters(filteredData);
      } else {
        setError('Failed to load filters');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
      setError('Failed to load filters');
      setLoading(false);
    }
  };

  const fetchStudentProgress = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await dashboardAPI.getStudentProgress({
        page: currentPage,
        per_page: Math.min(perPage, 100), // Enforce max 100
        year: selectedFilters.year,
        branch: selectedFilters.branch,
        domain: selectedFilters.domain,
        activity: selectedFilters.activity,
        search: selectedFilters.search || null,
        sort_by: currentSort.by,
        sort_order: currentSort.order,
      });

      if (response.success) {
        setStudents(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to load student progress');
        setStudents([]);
      }
    } catch (err) {
      console.error('Error fetching student progress:', err);
      setError('Failed to load student progress. Please try again.');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = useCallback((filterType, value) => {
    // Prevent clearing restricted filters for HODs
    if (filterType === 'branch' && allowedBranches && allowedBranches.length > 0 && !value) {
      return; // Don't allow clearing branch if HOD has restrictions
    }
    if (filterType === 'year' && allowedYears && allowedYears.length > 0 && !value) {
      return; // Don't allow clearing year if HOD has restrictions
    }
    
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
    setCurrentPage(1); // Reset to first page when filters change
  }, [allowedBranches, allowedYears]);

  // Handle search
  const handleSearch = useCallback(() => {
    setSelectedFilters(prev => ({
      ...prev,
      search: searchInput.trim(),
    }));
    setCurrentPage(1);
  }, [searchInput]);

  const handleSearchKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Handle sorting
  const handleSort = useCallback((column) => {
    setCurrentSort(prev => {
      if (prev.by === column) {
        return { by: column, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { by: column, order: 'asc' };
    });
    setCurrentPage(1);
  }, []);

  // Handle pagination
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    // Scroll to the table section instead of top of page
    const tableElement = document.querySelector('.student-progress-container');
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handlePerPageChange = useCallback((e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);

  // Handle export
  const handleExport = useCallback(async () => {
    try {
      const response = await dashboardAPI.exportStudentProgress({
        year: selectedFilters.year,
        branch: selectedFilters.branch,
        domain: selectedFilters.domain,
        activity: selectedFilters.activity,
        search: selectedFilters.search || null,
        sort_by: currentSort.by,
        sort_order: currentSort.order,
      });

      if (response.success) {
        // File download handled by the API function
        console.log('Export completed successfully');
      } else {
        // Show error dialog
        setShowExportDialog(true);
      }
    } catch (error) {
      console.error('Export failed:', error);
      setShowExportDialog(true);
    }
  }, [selectedFilters, currentSort]);

  const closeExportDialog = useCallback(() => {
    setShowExportDialog(false);
  }, []);

  // Clear all filters (but preserve HOD restrictions)
  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      year: null,
      branch: null,
      domain: null,
      activity: null,
      search: '',
    };
    
    // Preserve HOD-restricted filters
    if (allowedBranches && allowedBranches.length > 0) {
      clearedFilters.branch = allowedBranches[0]; // Keep first branch
    }
    if (allowedYears && allowedYears.length > 0) {
      clearedFilters.year = String(allowedYears[0]); // Keep first year
    }
    
    setSelectedFilters(clearedFilters);
    setSearchInput('');
    setCurrentPage(1);
  }, [allowedBranches, allowedYears]);

  // Render activity indicator
  const renderActivityIndicator = (student) => {
    if (student.is_active_today) {
      return (
        <span className="activity-badge active-today" title="Active Today">
          Active Today
        </span>
      );
    } else if (student.is_active_last_7_days) {
      return (
        <span className="activity-badge active-week" title="Active Last 7 Days">
          Active This Week
        </span>
      );
    } else {
      return (
        <span className="activity-badge inactive" title="Inactive">
          Inactive
        </span>
      );
    }
  };

  // Render progress bar
  const renderProgressBar = (percentage) => {
    const progress = Number(percentage) || 0;
    let colorClass = 'progress-low';
    
    if (progress >= 75) colorClass = 'progress-high';
    else if (progress >= 40) colorClass = 'progress-medium';
    
    return (
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className={`progress-fill ${colorClass}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="progress-text">{progress.toFixed(1)}%</span>
      </div>
    );
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (currentSort.by !== column) {
      return <ArrowUpDown size={16} className="sort-icon" />;
    }
    return currentSort.order === 'asc' 
      ? <ArrowUp size={16} className="sort-icon active" />
      : <ArrowDown size={16} className="sort-icon active" />;
  };

  // Render loading state
  if (loading && !students.length) {
    return (
      <div className="student-progress-container">
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
          <p>Loading student progress...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !students.length) {
    return (
      <div className="student-progress-container">
        <div className="error-container">
          <AlertCircle size={40} className="error-icon" />
          <p className="error-message">{error}</p>
          <button onClick={fetchStudentProgress} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="student-progress-container">
      <div className="student-progress-header">
        <div>
          <h2 className="section-title">Student Progress Tracker</h2>
          <p className="section-subtitle">
            Monitor student enrollment, activity, and progress across domains
          </p>
        </div>
        <button onClick={handleExport} className="btn btn-primary">
          <Download size={18} />
          Export Data
        </button>
      </div>

      {/* Filters Section */}
      {filters && (
        <StudentProgressFilters
          filters={filters}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          allowedBranches={allowedBranches}
          allowedYears={allowedYears}
        />
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="search-input"
          />
          <button onClick={handleSearch} className="btn btn-primary btn-sm">
            Search
          </button>
        </div>
        
        {pagination && (
          <div className="results-info">
            Showing {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, pagination.total_items)} of {pagination.total_items} students
          </div>
        )}
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <Loader2 className="spinner" size={32} />
            <p>Loading...</p>
          </div>
        ) : students.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} className="empty-icon" />
            <h3>No Students Found</h3>
            <p>Try adjusting your filters or search criteria</p>
          </div>
        ) : (
          <table className="student-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  Name {renderSortIcon('name')}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  Email {renderSortIcon('email')}
                </th>
                <th onClick={() => handleSort('year')} className="sortable">
                  Year {renderSortIcon('year')}
                </th>
                <th onClick={() => handleSort('branch')} className="sortable">
                  Branch {renderSortIcon('branch')}
                </th>
                <th onClick={() => handleSort('domain')} className="sortable">
                  Domain {renderSortIcon('domain')}
                </th>
                <th onClick={() => handleSort('progress')} className="sortable">
                  Progress {renderSortIcon('progress')}
                </th>
                <th>Activity Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, index) => (
                <tr key={`${student.email}-${index}`}>
                  <td className="student-name">{student.name}</td>
                  <td className="student-email">{student.email}</td>
                  <td className="student-year">Year {student.year}</td>
                  <td className="student-branch">{student.branch}</td>
                  <td className="student-domain">
                    <span className={student.domain === 'Not Enrolled' ? 'not-enrolled' : ''}>
                      {student.domain}
                    </span>
                  </td>
                  <td className="student-progress">
                    {renderProgressBar(student.progress_percentage)}
                  </td>
                  <td className="student-activity">
                    {renderActivityIndicator(student)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="pagination-container">
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={!pagination.has_prev}
              className="btn btn-icon"
              title="First Page"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.has_prev}
              className="btn btn-icon"
              title="Previous Page"
            >
              <ChevronLeft size={18} />
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-page ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.has_next}
              className="btn btn-icon"
              title="Next Page"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => handlePageChange(pagination.total_pages)}
              disabled={!pagination.has_next}
              className="btn btn-icon"
              title="Last Page"
            >
              <ChevronsRight size={18} />
            </button>
          </div>

          <div className="per-page-selector">
            <label>Rows per page:</label>
            <select value={perPage} onChange={handlePerPageChange} className="select-input">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      {showExportDialog && (
        <div className="modal-overlay" onClick={closeExportDialog}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Data</h3>
              <button onClick={closeExportDialog} className="modal-close" aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <AlertCircle size={48} className="modal-icon" />
              <p className="modal-message">Failed to export data. Please try again.</p>
            </div>
            <div className="modal-footer">
              <button onClick={closeExportDialog} className="btn btn-primary">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProgressTable;
